import { useCallback } from 'react';
import { SupportedEventMap, VideoEventEmitter } from './types';
import { isOverriddenEvent } from './utils';

export type UseVideoEventProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
  emitter: VideoEventEmitter;
};

/**
 * Exposes addEventListener/removeEventListener
 */
export default function useVideoEvents({
  videoRef,
  emitter,
}: UseVideoEventProps) {
  const addEventListener = useCallback(
    <TName extends keyof SupportedEventMap>(
      type: TName,
      listener: (this: HTMLVideoElement, ev: SupportedEventMap[TName]) => void,
    ) => {
      if (isOverriddenEvent(type)) {
        emitter.on(type, listener);
      } else {
        videoRef.current.addEventListener(type, listener);
      }
    },
    [emitter, videoRef],
  );

  const removeEventListener = useCallback(
    <TName extends keyof SupportedEventMap>(
      type: TName,
      listener: (this: HTMLVideoElement, ev: SupportedEventMap[TName]) => void,
    ) => {
      if (isOverriddenEvent(type)) {
        emitter.off(type, listener);
      } else {
        videoRef.current.removeEventListener(type, listener);
      }
    },
    [emitter, videoRef],
  );

  return { addEventListener, removeEventListener };
}
